import {FC, useCallback} from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {useBulkUnenrollUsers} from "@/app2/shared/data";
import {palette} from "@/constants";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";

interface IProps {
	open: boolean;
	onClose: () => void;
	/** After successful HTTP unenroll: clear grid selection (course row is already updated via query invalidation). */
	onSucceeded?: () => void;
	selectedUserIds: number[];
	courseId: number;
}

const buttonSx = {
	border: "none",
	boxShadow: "none",
	color: "primary.main",
	"&:hover": {
		backgroundColor: palette.orange08
	}
};

export const BulkUnenrollmentConfirmationModal: FC<IProps> = ({open, onClose, onSucceeded, selectedUserIds, courseId}) => {
	const {t} = useSSTranslation();
	const {successSnackbar, errorSnackbar, warningSnackbar} = useCustomSnackbar();
	const bulkUnenrollMutation = useBulkUnenrollUsers(courseId);

	const handleConfirm = useCallback(() => {
		if (!open) {
			return;
		}

		// Use HTTP mutation instead of WebSocket
		bulkUnenrollMutation.mutate(
			{userIds: selectedUserIds},
			{
				onSuccess: (response) => {
					const unenrolledCount = response.unenrolledUserIds?.length ?? 0;
					const errorCount = response.errors?.length ?? 0;

					if (errorCount === 0) {
						successSnackbar(t("bulk_unenrollment_success", "Unenrolled {{count}} user(s)", {count: unenrolledCount}));
					} else {
						const message = t("bulk_unenrollment_partial_success", "Unenrolled {{unenrolled}} user(s), {{failed}} failed", {
							unenrolled: unenrolledCount,
							failed: errorCount
						});
						if (unenrolledCount === 0) {
							errorSnackbar(message);
						} else {
							warningSnackbar(message);
						}
					}

					onSucceeded?.();
					onClose();
				},
				onError: (error) => {
					const message = error instanceof Error ? error.message : String(error);
					errorSnackbar(t("bulk_unenrollment_error", "Failed to unenroll users: {{message}}", {message}));
				}
			}
		);
	}, [open, selectedUserIds, bulkUnenrollMutation, successSnackbar, errorSnackbar, warningSnackbar, t, onClose, onSucceeded]);

	return (
		<Dialog
			open={open}
			onClose={(event, reason) => {
				if (reason !== "backdropClick") {
					onClose();
				}
			}}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			PaperProps={{
				sx: {
					bgcolor: "background.paperElevation3",
					borderColor: "background.paperElevation3"
				}
			}}
		>
			<DialogTitle id="alert-dialog-title">
				{t("bulk_unenrollment_modal_title", "Unenroll Users from Course", {count: selectedUserIds.length})}
			</DialogTitle>
			<DialogContent>
				<DialogContentText id="alert-dialog-description" variant="body1" color="text.primary">
					{t("bulk_unenrollment_modal_body_copy", "This will unenroll the selected users from the course. This action cannot be undone.", {count: selectedUserIds.length})}
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button variant="text" onClick={onClose} sx={buttonSx} disabled={bulkUnenrollMutation.isPending}>
					{t("team_progress_calendar_cancel", "Cancel")}
				</Button>
				<Button
					variant="text"
					onClick={handleConfirm}
					sx={buttonSx}
					disabled={bulkUnenrollMutation.isPending}
					startIcon={bulkUnenrollMutation.isPending ? <CircularProgress size={16} /> : undefined}
				>
					{bulkUnenrollMutation.isPending ? t("common_unenrolling", "Unenrolling...") : t("common_confirm", "Confirm")}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
